import { useFlags } from "launchdarkly-react-client-sdk";

import { useAppSelector } from "app/hooks";
import {
  AMBIENT_SCRIBE_CUSTOM_TEMPLATES,
  AMBIENT_SCRIBE_PAUSED_FEATURE,
  AMBIENT_SCRIBE_TEMPLATE_TYPES
} from "constants/feature-flags";
import { selectCurrentPractice } from "features/auth/auth-slice";
import useAfterVisitSummaryAccess from "features/medical-scribe/after-visit-summary/use-after-visit-summary-access.hook";
import {
  DEFAULT_AMBIENT_SCRIBE_QUERY,
  DEFAULT_AMBIENT_SCRIBE_QUERY_PAUSED,
  DEFAULT_AMBIENT_SCRIBE_QUERY_WITH_TEMPLATE,
  useGetAmbientScribeQuery,
  withAfterVisitSummaryProgress,
  withAudiologySummaryProgress,
  withConsultDebriefId
} from "services/ambient-scribe";
import {
  AMBIENT_SCRIBE_TEMPLATE_TYPE,
  type AmbientScribeTemplateType
} from "services/types/ambient-scribe-template.type";

type DetailQueryFeatures = {
  isPausedFeatureEnabled: boolean;
  isCustomTemplatesEnabled: boolean;
  isAudiologyMode: boolean;
  isConsultDebriefEnabled: boolean;
  isAvsEnabled: boolean;
};

/**
 * The scribe detail `?query=`. Each optional field is named only when its feature is on, so every
 * flag-off URL stays byte-identical and a backend that predates a field never sees it (RestQL
 * 400s the WHOLE read on an unknown field).
 */
export function buildScribeDetailQuery({
  isPausedFeatureEnabled,
  isCustomTemplatesEnabled,
  isAudiologyMode,
  isConsultDebriefEnabled,
  isAvsEnabled
}: DetailQueryFeatures) {
  // The paused-edit query is a superset: it adds chart_note (which resumePausedScribe needs) and
  // ambient_scribe_template_id.
  let query = DEFAULT_AMBIENT_SCRIBE_QUERY;
  if (isPausedFeatureEnabled) query = DEFAULT_AMBIENT_SCRIBE_QUERY_PAUSED;
  else if (isCustomTemplatesEnabled) {
    query = DEFAULT_AMBIENT_SCRIBE_QUERY_WITH_TEMPLATE;
  }
  if (isAudiologyMode) query = withAudiologySummaryProgress(query);
  // ENG-1707 replaced the consultCoachSignals LD flag with the practice's own setting.
  if (isConsultDebriefEnabled) query = withConsultDebriefId(query);
  if (isAvsEnabled) query = withAfterVisitSummaryProgress(query);
  return query;
}

type ScribeDetailQueryParams = {
  scribeId: number | undefined;
  /** Restricts the Scribe tab's template picker to one type (SN-19067). */
  templateType?: AmbientScribeTemplateType;
  /** With `templateType: "AudiologyReport"`, switches the Scribe tab to the report summary. */
  audiologyReportId?: number;
};

type ScribeDetailQueryOptions = {
  pollingInterval?: number;
  refetchOnMountOrArgChange?: boolean;
};

/**
 * The scribe details' read, and the feature switches that shape it. Everything that shows a
 * scribe's details goes through here, so the scribe details and Insert Scribe always subscribe
 * with identical args and share one cache entry: one request, and an Insert button that can never
 * read something different from what the details show.
 */
export default function useScribeDetailQuery(
  { scribeId, templateType, audiologyReportId }: ScribeDetailQueryParams,
  options: ScribeDetailQueryOptions = {}
) {
  const flags = useFlags();
  const isPausedFeatureEnabled = Boolean(
    flags[AMBIENT_SCRIBE_PAUSED_FEATURE] ?? false
  );
  const isTemplateTypesEnabled = Boolean(flags[AMBIENT_SCRIBE_TEMPLATE_TYPES]);
  const isAudiologyMode =
    isTemplateTypesEnabled &&
    templateType === AMBIENT_SCRIBE_TEMPLATE_TYPE.AUDIOLOGY_REPORT &&
    audiologyReportId != null;
  // Flag + scribe-paywall gate for the After Visit Summary tab (Intercom 215475744511616).
  // Deliberately no role gate — reviewing/sending the letter is front-desk work.
  const { isEnabled: isAvsEnabled } = useAfterVisitSummaryAccess();
  const currentPractice = useAppSelector(selectCurrentPractice);
  const isConsultDebriefEnabled = Boolean(
    currentPractice?.consult_debrief_enabled
  );

  const query = buildScribeDetailQuery({
    isPausedFeatureEnabled,
    isCustomTemplatesEnabled: Boolean(flags[AMBIENT_SCRIBE_CUSTOM_TEMPLATES]),
    isAudiologyMode,
    isConsultDebriefEnabled,
    isAvsEnabled
  });

  const { data, isLoading, isFetching, isError, refetch } =
    useGetAmbientScribeQuery(
      { id: Number(scribeId), query },
      { skip: !scribeId, ...options }
    );

  return {
    remote: data,
    isLoading,
    isFetching,
    isError,
    refetch,
    isTemplateTypesEnabled,
    isAudiologyMode,
    isAvsEnabled,
    isConsultDebriefEnabled
  };
}

export type ScribeDetailQuery = ReturnType<typeof useScribeDetailQuery>;
